import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import ReactSelector from "../common/CommonReactSelector";
import { filterGroupsByUserAccess, normalizeGroups } from "../task/GroupAssignParts";
import { loadInventoryGroups } from "./inventoryGroupScope";
import { updateInventoryItem } from "../../service/MeterInventoryManagement";
import {
  FormField,
  apiErrorMessage,
  apiSuccessMessage,
  statusOptionsFor,
  useInventoryScope,
  useInventoryStores,
} from "./inventoryFormShared";

const idOf = (value) => (value === undefined || value === null || value === "" ? "" : String(value));

export const currentInventoryEditValues = (item = {}) => ({
  status: idOf(item.status),
  storeId: idOf(item.storeId ?? item.store?.id ?? item.store?.storeId),
  groupId: idOf(item.groupAssignmentDetails?.groupId ?? item.groupId),
});

// Only changed fields are sent; unchanged or emptied fields are left out.
export const buildInventoryItemUpdate = (item, form) => {
  const current = currentInventoryEditValues(item);
  const payload = { inventoryItemId: item?.id ?? item?.inventoryItemId };
  if (form.status && form.status !== current.status) payload.status = form.status;
  if (form.storeId && form.storeId !== current.storeId) payload.storeId = Number(form.storeId);
  if (form.groupId && form.groupId !== current.groupId) payload.groupId = Number(form.groupId);
  return payload;
};

const withCurrentOption = (options, value, label) =>
  value && !options.some((option) => option.value === value)
    ? [{ value, label: label || value }, ...options]
    : options;

const selectProps = {
  isSearchable: true,
  height: "40px",
  borderRadius: "8px",
  fontSize: "14px",
  menuPortalTarget: document.body,
  menuPosition: "fixed",
};

export const InventoryItemEditForm = ({ item, onCancel, onSaved }) => {
  const { theme, langMode, departmentId } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const { projectId, organizationId } = useInventoryScope();
  const { storeOptions: activeStoreOptions, loading: storesLoading } = useInventoryStores(true, projectId, langMode);

  const [form, setForm] = useState(() => currentInventoryEditValues(item));
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setGroupsLoading(true);
    loadInventoryGroups(Number(organizationId), departmentId)
      .then((raw) => {
        if (!cancelled) setGroups(normalizeGroups(filterGroupsByUserAccess(raw)));
      })
      .catch(() => {
        if (!cancelled) toast.error(t.inventoryTransferGroupLoadFailed);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, departmentId]);

  const current = currentInventoryEditValues(item);
  const statusOptions = useMemo(() => statusOptionsFor(t), [t]);
  const storeOptions = useMemo(
    () => withCurrentOption(
      activeStoreOptions.map((option) => ({ ...option, value: idOf(option.value) })),
      current.storeId,
      item.storeName ?? item.location ?? item.storeLocation
    ),
    [activeStoreOptions, current.storeId, item]
  );
  const groupOptions = useMemo(
    () => withCurrentOption(
      groups.map((group) => ({ value: idOf(group.groupId), label: group.groupName })),
      current.groupId,
      item.groupAssignmentDetails?.groupName ?? item.groupAssignment
    ),
    [groups, current.groupId, item]
  );

  const payload = buildInventoryItemUpdate(item, form);
  const hasChanges = Object.keys(payload).length > 1;

  const setField = (key) => (option) => {
    setForm((prev) => ({ ...prev, [key]: idOf(option?.value) }));
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      const response = await updateInventoryItem(payload);
      if (!mountedRef.current) return;
      toast.success(apiSuccessMessage(response, t.inventoryEditSuccess));
      onSaved(response);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err?.response ? apiErrorMessage(err, "") : "";
      if (message) toast.error(message);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#CFE3FB] bg-[#F5F9FE] p-4 space-y-3" data-testid="inventory-edit-form">
      <FormField label={t.inventoryEditStatus}>
        <ReactSelector
          {...selectProps}
          options={statusOptions}
          value={statusOptions.find((option) => option.value === form.status) || null}
          onChange={setField("status")}
          isDisabled={saving}
          dataTestId="inventory-edit-status"
        />
      </FormField>
      <FormField label={t.inventoryEditStore}>
        <ReactSelector
          {...selectProps}
          options={storeOptions}
          value={storeOptions.find((option) => option.value === form.storeId) || null}
          onChange={setField("storeId")}
          placeholder={storesLoading ? t.Processing : t.inventoryImportSelectStorePlaceholder}
          isDisabled={saving || storesLoading}
          dataTestId="inventory-edit-store"
        />
      </FormField>
      <FormField label={t.inventoryEditGroup}>
        <ReactSelector
          {...selectProps}
          options={groupOptions}
          value={groupOptions.find((option) => option.value === form.groupId) || null}
          onChange={setField("groupId")}
          placeholder={groupsLoading ? t.Processing : t.inventoryEditSelectGroup}
          isDisabled={saving || groupsLoading}
          dataTestId="inventory-edit-group"
        />
      </FormField>
      <div className="flex justify-end gap-2 pt-1">
        <CommonButton colorIntent="danger" theme={theme} variant="outline" onClick={onCancel} disabled={saving} data-testid="inventory-edit-cancel">
          {t.cancel}
        </CommonButton>
        <CommonButton colorIntent="success" theme={theme} variant="primary" onClick={handleSave} disabled={!hasChanges || saving} data-testid="inventory-edit-save">
          {saving ? t.Processing : t.inventoryEditSave}
        </CommonButton>
      </div>
    </div>
  );
};

export default InventoryItemEditForm;
